"use client";

import { Suspense, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { withDeviceHeader } from "@/lib/deviceIdentity";
import {
  buildLoginHref,
  safeAuthReturnPath,
  withAuthReturnPath,
} from "@/lib/authRedirect";

export default function SignupPage() {
  return (
    <Suspense fallback={null}>
      <SignupPageInner />
    </Suspense>
  );
}

function SignupPageInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnPath = safeAuthReturnPath(searchParams.get("redirect"));

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // web-www 의 히어로 이메일 capture → /signup?email=... 로 넘어왔을 때 1단계 prefill
  useEffect(() => {
    const prefill = searchParams.get("email");
    if (prefill && !email) setEmail(prefill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [verificationCode, setVerificationCode] = useState("");

  const [errorMSG, setErrorMSG] = useState("");
  const [loading, setLoading] = useState(false);

  // Step 1: Send Verification
  const handleSendVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMSG("");

    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000"}/api/auth/register`,
        {
          method: "POST",
          headers: withDeviceHeader({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            email,
            username,
            password: "TEMPORARY_PASSWORD_DO_NOT_USE",
          }),
        },
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data?.error?.message ?? data.message ?? "Registration failed",
        );
      }

      setStep(2);
    } catch (err: any) {
      setErrorMSG(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify Code
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMSG("");

    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000"}/api/auth/verify-email`,
        {
          method: "POST",
          headers: withDeviceHeader({ "Content-Type": "application/json" }),
          body: JSON.stringify({ email, code: verificationCode }),
        },
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data?.error?.message ?? data.message ?? "Verification failed",
        );
      }

      setStep(3);
    } catch (err: any) {
      setErrorMSG(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Set Password
  const handleCompleteSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setErrorMSG("Passwords do not match");
      return;
    }

    setLoading(true);
    setErrorMSG("");

    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000"}/api/auth/complete-signup`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        },
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data?.error?.message ?? data.message ?? "Failed to set password",
        );
      }

      router.push(withAuthReturnPath("/login?verified=true", returnPath));
    } catch (err: any) {
      setErrorMSG(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-[100dvh] w-full items-center justify-center bg-gray-50 dark:bg-background flex-col">
      <div className="w-full max-w-[400px] bg-white dark:bg-[#161618] border border-gray-200 dark:border-[#2a2a2e] rounded-[24px] shadow-sm p-8 flex flex-col items-center">
        <div className="flex flex-col items-center justify-center mb-6">
          <img
            src="/images/seexr-light.png"
            alt="Seexr Logo"
            className="h-10 w-auto object-contain dark:hidden"
          />
          <img
            src="/images/seexr-dark.png"
            alt="Seexr Logo"
            className="h-10 w-auto object-contain hidden dark:block"
          />
        </div>

        {step === 1 && (
          <>
            <h1 className="text-2xl font-black text-gray-900 dark:text-gray-100 tracking-tight mb-2">
              Create an account
            </h1>
            <p className="text-[14px] text-gray-500 dark:text-gray-400 mb-8 text-center">
              Sign up to get started
            </p>

            {errorMSG && (
              <div className="w-full bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/20 text-[13px] font-semibold p-3 rounded-lg mb-4 text-center">
                {errorMSG}
              </div>
            )}

            <form
              onSubmit={handleSendVerification}
              className="w-full flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-300">
                  Name
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="w-full h-[40px] px-3 rounded-lg border border-gray-200 dark:border-[#2a2a2e] dark:bg-[#0c0c0e] dark:text-gray-100 text-[14px] outline-none focus:border-seexr-primary focus:ring-1 focus:ring-seexr-primary transition-all"
                  placeholder="Your Name"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-300">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full h-[40px] px-3 rounded-lg border border-gray-200 dark:border-[#2a2a2e] dark:bg-[#0c0c0e] dark:text-gray-100 text-[14px] outline-none focus:border-seexr-primary focus:ring-1 focus:ring-seexr-primary transition-all"
                  placeholder="name@company.com"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full h-[44px] mt-2 rounded-full bg-seexr-solid text-white text-[14px] font-medium tracking-wide transition-colors hover:bg-seexr-hover shadow-sm disabled:opacity-50 flex justify-center items-center"
              >
                {loading ? (
                  <span className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin"></span>
                ) : (
                  "Send Verification Code"
                )}
              </button>
            </form>

            <div className="mt-6 text-[13px] text-gray-500 dark:text-gray-400 text-center">
              Already have an account?{" "}
              <Link
                href={buildLoginHref(returnPath)}
                className="font-semibold text-seexr-primary hover:text-seexr-hover dark:text-[#225D30] dark:hover:text-[#194523]"
              >
                Sign in
              </Link>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h1 className="text-2xl font-black text-gray-900 dark:text-gray-100 tracking-tight mb-2">
              Check your email
            </h1>
            <p className="text-[14px] text-gray-500 dark:text-gray-400 mb-8 text-center px-4">
              We've sent a 6-digit verification code to <strong>{email}</strong>
            </p>

            {errorMSG && (
              <div className="w-full bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/20 text-[13px] font-semibold p-3 rounded-lg mb-4 text-center">
                {errorMSG}
              </div>
            )}

            <form
              onSubmit={handleVerify}
              className="w-full flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-300 text-center">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  required
                  maxLength={6}
                  className="w-full h-[44px] px-3 rounded-lg border border-gray-200 dark:border-[#2a2a2e] dark:bg-[#0c0c0e] dark:text-gray-100 text-[18px] tracking-widest text-center font-bold outline-none focus:border-seexr-primary focus:ring-1 focus:ring-seexr-primary transition-all"
                  placeholder="000000"
                />
              </div>
              <button
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                className="w-full h-[44px] mt-2 rounded-full bg-seexr-solid text-white text-[14px] font-medium tracking-wide transition-colors hover:bg-seexr-hover shadow-sm disabled:opacity-50 flex justify-center items-center"
              >
                {loading ? (
                  <span className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin"></span>
                ) : (
                  "Verify Email"
                )}
              </button>
            </form>
          </>
        )}

        {step === 3 && (
          <>
            <h1 className="text-2xl font-black text-gray-900 dark:text-gray-100 tracking-tight mb-2">
              Set up a Password
            </h1>
            <p className="text-[14px] text-gray-500 dark:text-gray-400 mb-8 text-center">
              Secure your new account
            </p>

            {errorMSG && (
              <div className="w-full bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/20 text-[13px] font-semibold p-3 rounded-lg mb-4 text-center">
                {errorMSG}
              </div>
            )}

            <form
              onSubmit={handleCompleteSignup}
              className="w-full flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-300">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full h-[40px] px-3 rounded-lg border border-gray-200 dark:border-[#2a2a2e] dark:bg-[#0c0c0e] dark:text-gray-100 text-[14px] outline-none focus:border-seexr-primary focus:ring-1 focus:ring-seexr-primary transition-all"
                  placeholder="••••••••"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[13px] font-semibold text-gray-700 dark:text-gray-300">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full h-[40px] px-3 rounded-lg border border-gray-200 dark:border-[#2a2a2e] dark:bg-[#0c0c0e] dark:text-gray-100 text-[14px] outline-none focus:border-seexr-primary focus:ring-1 focus:ring-seexr-primary transition-all"
                  placeholder="••••••••"
                />
              </div>
              <button
                type="submit"
                disabled={
                  loading ||
                  password.length === 0 ||
                  confirmPassword.length === 0
                }
                className="w-full h-[44px] mt-2 rounded-full bg-seexr-solid text-white text-[14px] font-medium tracking-wide transition-colors hover:bg-seexr-hover shadow-sm disabled:opacity-50 flex justify-center items-center"
              >
                {loading ? (
                  <span className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin"></span>
                ) : (
                  "Complete Registration"
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
